import type { TemplateContext } from "@unbox/api";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Download01,
  FilterLines,
  Mail01,
  Plus,
  SearchLg,
  Upload01,
  UserPlus01,
} from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import type { Selection } from "react-aria-components";
import { useController, useFormContext } from "react-hook-form";
import { useCreatorCohorts } from "@/api/creator-cohorts";
import { usePagination } from "@/api/pagination";
import {
  type MerchantRecommendedCreator,
  useDownloadRecommendationsCsvTemplate,
  useImportRecommendationsPreview,
  useMerchantRecommendations,
} from "@/api/recommendations";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Table, TableCard } from "@/components/application/table/table";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Input } from "@/components/base/input/input";
import { ProgressBarBase } from "@/components/base/progress-indicators/progress-indicators";
import { FilterPills } from "@/components/filters/filter-pills";
import { ImportCSV } from "@/components/ImportCSV";
import { SAMPLE_PREVIEW_CONTEXT } from "@/components/message-composer/composer-preview";
import { MessageComposerField } from "@/components/message-composer/message-composer-field";
import { useClearableDebounce } from "@/hooks/use-debounce";
import { useMerchantName } from "@/hooks/use-merchant-name";
import { CreatorTag } from "@/pages/creator-tags/tag";
import { FiltersProvider, useFilters } from "@/providers/filter-provider";
import type { FilterField } from "@/providers/filter-registry";
import { error as errorToast } from "@/providers/ToastProvider";
import { cx } from "@/utils/cx";
import { type CreateCollabFormValues, MAX_INVITATION_MESSAGE_LENGTH } from "../create-collab-group-modal";
import { InvitationFilterPanel } from "./InvitationFilterPanel";

type RecommendationImportRow = MerchantRecommendedCreator & {
  handle: string;
  name: string;
  avatarUrl: string | null;
  cohortName: string | null;
};

const INVITATION_FILTER_FIELDS: FilterField[] = [
  "minGmv",
  "maxGmv",
  "minFollowers",
  "maxFollowers",
  "maleAudiencePercent",
  "femaleAudiencePercent",
  "age",
];

type InvitationStep4Props = {
  showCreatorSelection?: boolean;
  showWhatsapp?: boolean;
  messageReadOnly?: boolean;
};

export const InvitationStep4 = ({
  showCreatorSelection = true,
  showWhatsapp = true,
  messageReadOnly = false,
}: InvitationStep4Props) => (
  <div className="flex flex-col gap-6 pb-6">
    {showCreatorSelection && (
      <>
        <FiltersProvider activeFields={INVITATION_FILTER_FIELDS}>
          <CreatorSelection />
        </FiltersProvider>
        <hr className="border-0 border-t border-border-secondary" />
      </>
    )}
    <InvitationDetails showWhatsapp={showWhatsapp} messageReadOnly={messageReadOnly} />
  </div>
);

const CreatorSelection = () => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CreateCollabFormValues>();
  const invitedCreatorIds = watch("invitedCreatorIds");
  const invitedCreators = watch("invitedCreators");

  const [search, setSearch] = useState("");
  const { value: debouncedSearch } = useClearableDebounce(search);
  const [selectedCohortIds, setSelectedCohortIds] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { apiFilters } = useFilters();
  const pagination = usePagination({ limit: 10, offset: 0 });
  const { data: cohorts = [] } = useCreatorCohorts();
  const { data: recommendationsData, isLoading } = useMerchantRecommendations({
    pagination: pagination.pagination,
    allCreators: true,
    filters: {
      text: debouncedSearch,
      includeCohortIds: selectedCohortIds.length ? selectedCohortIds : undefined,
      ...apiFilters,
    },
  });
  const candidates = recommendationsData?.recommendations ?? [];
  const totalCount = recommendationsData?.total ?? 0;

  useEffect(() => {
    pagination.setPage(1);
  }, [apiFilters]);

  const toggleCohort = (cohortId: string) => {
    setSelectedCohortIds((prev) =>
      prev.includes(cohortId) ? prev.filter((id) => id !== cohortId) : [...prev, cohortId],
    );
    pagination.setPage(1);
  };

  const toCreatorDetail = (creator: (typeof candidates)[number] | RecommendationImportRow) => {
    const displayName = creator.nickname || creator.username;
    const initials = displayName
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
    return {
      id: creator.tiktokCreatorOpenId,
      name: displayName,
      handle: `@${creator.username}`,
      avatar: creator.avatar ?? null,
      initials,
      cohortId: creator.cohortId,
      // Only CSV rows carry a name to create (when the CSV's cohort didn't match an existing
      // one); manual table rows only ever have an already-resolved cohortId, never this.
      pendingCohortName: creator.cohortId ? null : "cohortName" in creator ? (creator.cohortName ?? null) : null,
      isCreator: creator.isCreator,
      previouslyInvited: creator.previouslyInvited,
      hasActiveCollaboration: (creator.collaborationCount ?? 0) > 0,
    };
  };

  const handleSelectionChange = (keys: Selection) => {
    const pageIds = candidates.map((c) => c.tiktokCreatorOpenId);
    const selectedOnPage = keys === "all" ? new Set(pageIds) : (keys as Set<string>);

    const otherPagesIds = invitedCreatorIds.filter((id) => !pageIds.includes(id));
    const thisPageIds = pageIds.filter((id) => selectedOnPage.has(id));
    setValue("invitedCreatorIds", [...otherPagesIds, ...thisPageIds], { shouldDirty: true });

    const otherPagesCreators = invitedCreators.filter((c) => !pageIds.includes(c.id));
    const thisPageCreators = candidates.filter((c) => selectedOnPage.has(c.tiktokCreatorOpenId)).map(toCreatorDetail);
    setValue("invitedCreators", [...otherPagesCreators, ...thisPageCreators], { shouldDirty: true });
  };

  const handleCsvImported = (imported: RecommendationImportRow[]) => {
    const existingIds = new Set(invitedCreatorIds);
    const newOnes = imported.filter((c) => !existingIds.has(c.tiktokCreatorOpenId));
    setValue("invitedCreatorIds", [...invitedCreatorIds, ...newOnes.map((c) => c.tiktokCreatorOpenId)], {
      shouldDirty: true,
    });
    setValue("invitedCreators", [...invitedCreators, ...newOnes.map(toCreatorDetail)], { shouldDirty: true });
  };

  const downloadCsvTemplate = useDownloadRecommendationsCsvTemplate();
  const handleDownloadTemplate = async () => {
    try {
      await downloadCsvTemplate();
    } catch {
      errorToast("Could not download the template");
    }
  };
  const { mutateAsync: previewCsv } = useImportRecommendationsPreview();

  const handleImportPreview = async (file: File): Promise<RecommendationImportRow[]> => {
    const csv = await file.text();
    const { recommendations } = await previewCsv(csv);
    return recommendations.map((r) => ({
      ...r,
      handle: r.username,
      name: r.nickname || r.username,
      avatarUrl: r.avatar,
    }));
  };

  const handleImportSave = async (selectedRows: RecommendationImportRow[]) => {
    handleCsvImported(selectedRows);
    return { async: false as const, imported: selectedRows.length };
  };

  const alreadyInCollabCount = useMemo(
    () => invitedCreators.filter((c) => c.hasActiveCollaboration).length,
    [invitedCreators],
  );
  const invitedCreatorIdsSet = useMemo(() => new Set(invitedCreatorIds), [invitedCreatorIds]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="text-md font-semibold text-primary">Invite People</p>
        <p className="text-sm text-quaternary">Introduce yourself and tell creators why you'd love to work with them</p>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Input
            icon={SearchLg}
            placeholder="Search"
            value={search}
            onChange={(v) => {
              setSearch(v);
              pagination.setPage(1);
            }}
          />
        </div>
        <Button color="secondary" size="sm" iconLeading={FilterLines} onClick={() => setFiltersOpen(true)}>
          Filter
        </Button>

        <ImportCSV<RecommendationImportRow, { imported: number }>
          className="z-[140]"
          columns={["creator_handle"]}
          onDownloadTemplate={handleDownloadTemplate}
          onPreview={handleImportPreview}
          onSave={handleImportSave}
          getSuccessMessage={(result) =>
            `Added ${result.imported} creator${result.imported === 1 ? "" : "s"} to the invite list.`
          }
          renderTrigger={(openFilePicker, isPreviewing) => (
            <Dropdown.Root>
              <Button color="secondary" size="sm" iconTrailing={ChevronDown} isLoading={isPreviewing}>
                CSV Actions
              </Button>
              <Dropdown.Popover>
                <Dropdown.Menu>
                  <Dropdown.Item icon={Upload01} onClick={openFilePicker}>
                    Import CSV
                  </Dropdown.Item>
                  <Dropdown.Item icon={Download01} onClick={handleDownloadTemplate}>
                    Download template
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          )}
        />
      </div>

      <FilterPills className="-my-2" />

      <div style={{ "--sidebar-top-offset": "0px" } as React.CSSProperties}>
        <InvitationFilterPanel open={filtersOpen} close={() => setFiltersOpen(false)} />
      </div>

      <div className="bg-white border border-border-secondary rounded-xl px-4 py-4 flex flex-col gap-3">
        <span className="text-sm font-medium text-secondary">Quick Filter Creator By Cohort:</span>
        <div className="flex flex-wrap gap-2">
          {cohorts.map((cohort) => {
            const isActive = selectedCohortIds.includes(cohort.id);
            return (
              <button
                key={cohort.id}
                type="button"
                onClick={() => toggleCohort(cohort.id)}
                className={cx(
                  "flex items-center gap-1 px-4 py-1.5 rounded-full border text-sm font-medium transition-colors",
                  isActive
                    ? "bg-success-50 border-success-200 text-success-700"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50",
                )}
              >
                {isActive ? <Check className="w-4 h-4 shrink-0" /> : <Plus className="w-3 h-3 shrink-0" />}
                {cohort.name}
              </button>
            );
          })}
        </div>
      </div>

      <TableCard.Root size="sm">
        <Table
          aria-label="Recommended creators"
          size="sm"
          selectionMode="multiple"
          selectedKeys={invitedCreatorIdsSet}
          onSelectionChange={handleSelectionChange}
        >
          <Table.Header allowSelectAll>
            <Table.Head id="name" label="Name" isRowHeader className="min-w-[280px]" />
            <Table.Head id="matchQuality" label="Match Quality" className="min-w-[160px]" />
            <Table.Head id="postRate" label="Post Rate" className="min-w-[100px]" />
            <Table.Head id="cohort" label="Cohort" className="min-w-[140px]" />
          </Table.Header>
          <Table.Body
            items={candidates}
            // react-aria-components caches each rendered row by item reference, so list every
            // closure value the renderer reads here — otherwise the isSelected-driven warning cell
            // only updates on some unrelated invalidation instead of right after (un)selecting a row.
            dependencies={[invitedCreatorIdsSet]}
            renderEmptyState={() => (
              <div className="flex items-center justify-center h-[72px] text-sm text-tertiary">
                {isLoading ? "Loading…" : "No creators found"}
              </div>
            )}
          >
            {(creator) => {
              const detail = toCreatorDetail(creator);
              const cohort = creator.cohortId ? cohorts.find((c) => c.id === creator.cohortId) : undefined;
              const isInAnotherCollab = (creator.collaborationCount ?? 0) > 0;
              const isSelected = invitedCreatorIdsSet.has(creator.tiktokCreatorOpenId);

              return (
                <Table.Row id={creator.tiktokCreatorOpenId}>
                  <Table.Cell>
                    <div className="flex items-center gap-3">
                      <Avatar src={creator.avatar ?? undefined} initials={detail.initials} size="md" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-primary truncate">{detail.name}</span>
                        <span className="text-sm text-tertiary truncate">{detail.handle}</span>
                      </div>
                    </div>
                  </Table.Cell>

                  {isInAnotherCollab && isSelected ? (
                    <Table.Cell colSpan={3}>
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="size-4 text-warning-600 shrink-0" />
                        <span className="text-xs font-medium text-warning-primary">
                          This creator is already in another collab. Adding them here will remove them from it.
                        </span>
                      </div>
                    </Table.Cell>
                  ) : (
                    <>
                      <Table.Cell>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-secondary whitespace-nowrap">
                            {creator.matchQualityScore}%
                          </span>
                          <ProgressBarBase
                            value={creator.matchQualityScore}
                            className="w-16 h-1.5"
                            progressClassName="bg-brand-900"
                          />
                        </div>
                      </Table.Cell>

                      <Table.Cell>
                        <span className="text-sm font-medium text-secondary">
                          {creator.postRate == null ? "—" : `${Math.round(creator.postRate * 100)}%`}
                        </span>
                      </Table.Cell>

                      <Table.Cell>
                        {cohort ? <CreatorTag tag={cohort} /> : <span className="text-sm text-tertiary">—</span>}
                      </Table.Cell>
                    </>
                  )}
                </Table.Row>
              );
            }}
          </Table.Body>
        </Table>
        <PaginationPageMinimalCenter
          page={pagination.page}
          total={Math.ceil(totalCount / pagination.pagination.limit)}
          onPageChange={pagination.setPage}
          className="px-4 py-3 md:px-6 md:pt-2 md:pb-2 bg-primary"
        />
      </TableCard.Root>

      {alreadyInCollabCount > 0 && (
        <div className="bg-brand-solid rounded-lg p-4 flex items-start gap-2">
          <UserPlus01 className="size-5 text-white shrink-0" />
          <span className="text-sm font-medium text-white">
            You have selected {invitedCreators.length} creators to invite to this collab. {alreadyInCollabCount} are
            currently in other collabs. Inviting them will move them here.
          </span>
        </div>
      )}

      {errors.invitedCreatorIds?.message && (
        <p className="text-sm text-error-primary">{errors.invitedCreatorIds.message}</p>
      )}
    </div>
  );
};

const InvitationDetails = ({ showWhatsapp, messageReadOnly }: { showWhatsapp: boolean; messageReadOnly: boolean }) => {
  const { control, watch } = useFormContext<CreateCollabFormValues>();
  const invitationMessage = watch("invitationMessage");
  const { field: emailField, fieldState: emailFieldState } = useController({ name: "sellerEmail", control });
  const { field: whatsappField, fieldState: whatsappFieldState } = useController({
    name: "whatsappNumber",
    control,
  });

  const shopName = useMerchantName();

  const previewContext: TemplateContext = shopName ? { ...SAMPLE_PREVIEW_CONTEXT, shopName } : SAMPLE_PREVIEW_CONTEXT;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <p className="text-md font-semibold text-primary">Invitation Message</p>
          <p className="text-sm text-quaternary">
            {messageReadOnly
              ? "The message sent to creators when they're invited. Locked once the collaboration is published."
              : "Introduce yourself and tell creators why you'd love to work with them"}
          </p>
        </div>
        {messageReadOnly ? (
          <div className="rounded-lg bg-secondary px-3.5 py-3 text-md text-primary whitespace-pre-wrap min-h-[6rem]">
            {invitationMessage || <span className="text-tertiary italic">No message set</span>}
          </div>
        ) : (
          <MessageComposerField
            control={control}
            name="invitationMessage"
            variableContext="collab-invitation"
            previewContext={previewContext}
            maxLength={MAX_INVITATION_MESSAGE_LENGTH}
            minHeight={160}
            aria-label="Invitation message"
          />
        )}
      </div>

      <hr className="border-0 border-t border-border-secondary" />

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <p className="text-md font-semibold text-primary">Contact Info</p>
          <p className="text-sm text-quaternary">How creators can reach you</p>
        </div>
        <div className="flex gap-4">
          <div className="flex-1 flex flex-col gap-1.5">
            <Input
              label="Email Address"
              isRequired
              type="email"
              icon={Mail01}
              placeholder="you@company.com"
              isInvalid={!!emailFieldState.error}
              hint={emailFieldState.error?.message}
              {...emailField}
            />
          </div>
          {showWhatsapp && (
            <div className="flex-1 flex flex-col gap-1.5">
              <Input
                label="Phone Number"
                type="tel"
                placeholder="Enter phone number"
                isInvalid={!!whatsappFieldState.error}
                hint={whatsappFieldState.error?.message}
                {...whatsappField}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
